// Single-date picker — house react-day-picker dark styling (matches
// DateRangePicker). Emits YYYY-MM-DD. Supports future-date disabling (DOB /
// contact date) and a year/month dropdown for far-back dates like birthdays.
// The calendar renders in a portal with fixed positioning so a scrolling or
// overflow-hidden ancestor cannot clip it, and it opens upward when the space
// below the field is too short (e.g. the eDM Schedule card at the page end).
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { DayPicker } from 'react-day-picker';
import { format, parse, isValid } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import 'react-day-picker/dist/style.css';

interface DatePickerFieldProps {
  /** YYYY-MM-DD or null/undefined. */
  value?: string | null;
  /** Receives YYYY-MM-DD, or '' when cleared. */
  onChange: (value: string) => void;
  label?: string;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Disallow future dates (DOB, contact date). */
  disableFuture?: boolean;
  /** Year/month dropdown navigation — good for birthdays. */
  showYearDropdown?: boolean;
  fromYear?: number;
  toYear?: number;
  /** Tighter control for inline use in drawers. */
  compact?: boolean;
}

const RDP_DARK_STYLE = `
  .rdp { --rdp-cell-size: 36px; --rdp-accent-color: #2563eb; --rdp-background-color: #1f2937; margin: 0; }
  .rdp-month { margin: 0; }
  .rdp-caption { display: flex; justify-content: center; align-items: center; gap: .5rem; padding: 0 .5rem; margin-bottom: .5rem; }
  .rdp-caption_label { font-size: .875rem; font-weight: 500; color: white; }
  .rdp-caption_dropdowns { display: flex; gap: .25rem; }
  .rdp-dropdown { background: #374151; color: white; border: 1px solid #4b5563; border-radius: 4px; padding: 2px 4px; font-size: .8rem; }
  .rdp-dropdown_month, .rdp-dropdown_year { background: #1f2937; color: white; }
  .rdp-nav { display: flex; gap: .25rem; }
  .rdp-nav_button { width: 28px; height: 28px; background: transparent; border: none; border-radius: 4px; color: #9ca3af; cursor: pointer; display: flex; align-items: center; justify-content: center; }
  .rdp-nav_button:hover { background: #374151; color: white; }
  .rdp-table { border-collapse: collapse; }
  .rdp-head_cell { font-size: .75rem; font-weight: normal; color: #9ca3af; padding: .25rem 0; text-align: center; width: var(--rdp-cell-size); }
  .rdp-cell { padding: 0; }
  .rdp-day { width: var(--rdp-cell-size); height: var(--rdp-cell-size); font-size: .875rem; color: #d1d5db; border-radius: 6px; cursor: pointer; background: transparent; border: none; }
  .rdp-day:hover:not(.rdp-day_selected):not(.rdp-day_disabled) { background: #374151; }
  .rdp-day_today { border: 1px solid #3b82f6; color: #60a5fa; }
  .rdp-day_selected { background: #2563eb !important; color: white !important; }
  .rdp-day_outside { color: #4b5563; }
  .rdp-day_disabled { color: #4b5563; cursor: not-allowed; }
`;

const POPOVER_GAP = 8;

function parseYMD(s: string | null | undefined): Date | undefined {
  if (!s) return undefined;
  const d = parse(s, 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : undefined;
}

export function DatePickerField({
  value,
  onChange,
  label,
  id = 'date-field',
  placeholder = 'Select date',
  disabled = false,
  disableFuture = false,
  showYearDropdown = false,
  fromYear,
  toYear,
  compact = false,
}: DatePickerFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const selected = parseYMD(value);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && setIsOpen(false);
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen]);

  // Place the popover against the trigger: below when it fits, else above when
  // there is more room there; clamped inside the viewport. Re-placed on any
  // scroll (capture, so the app's scrolling <main> counts) and on resize.
  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    const place = () => {
      const button = buttonRef.current;
      const popover = popoverRef.current;
      if (!button || !popover) return;
      const r = button.getBoundingClientRect();
      const h = popover.offsetHeight;
      const w = popover.offsetWidth;
      const spaceBelow = window.innerHeight - r.bottom - POPOVER_GAP;
      const spaceAbove = r.top - POPOVER_GAP;
      const top = spaceBelow < h && spaceAbove > spaceBelow ? Math.max(POPOVER_GAP, r.top - h - POPOVER_GAP) : r.bottom + POPOVER_GAP;
      const left = Math.max(POPOVER_GAP, Math.min(r.left, window.innerWidth - w - POPOVER_GAP));
      setPosition({ top, left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [isOpen]);

  const today = new Date();
  const resolvedToYear = toYear ?? today.getFullYear();
  const resolvedFromYear = fromYear ?? today.getFullYear() - 100;

  return (
    <div className="relative" ref={containerRef}>
      {label && (
        <label htmlFor={id} className="block text-[11px] uppercase tracking-wide text-gray-500 font-bold mb-1">
          {label}
        </label>
      )}
      <button
        ref={buttonRef}
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((o) => !o)}
        className={`w-full flex items-center justify-between bg-gray-800 border border-gray-700 rounded ${
          compact ? 'px-2 py-1.5 text-sm' : 'px-3 py-2'
        } text-white focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-gray-750 transition-colors disabled:opacity-40`}
      >
        <span className={selected ? 'text-white' : 'text-gray-500'}>
          {selected ? format(selected, 'dd/MM/yyyy') : placeholder}
        </span>
        <CalendarIcon className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
      </button>

      {isOpen && createPortal(
        <div
          ref={popoverRef}
          className="fixed bg-gray-800 border border-gray-600 rounded-lg shadow-xl p-3"
          style={{ top: position?.top ?? 0, left: position?.left ?? 0, zIndex: 9000, visibility: position ? 'visible' : 'hidden' }}
        >
          <style>{RDP_DARK_STYLE}</style>
          <DayPicker
            mode="single"
            selected={selected}
            onSelect={(d) => {
              onChange(d ? format(d, 'yyyy-MM-dd') : '');
              if (d) setIsOpen(false);
            }}
            defaultMonth={selected ?? (disableFuture ? today : undefined)}
            disabled={disableFuture ? { after: today } : undefined}
            captionLayout={showYearDropdown ? 'dropdown-buttons' : 'buttons'}
            fromYear={showYearDropdown ? resolvedFromYear : undefined}
            toYear={showYearDropdown ? resolvedToYear : undefined}
            showOutsideDays
          />
          <div className="mt-3 flex justify-between border-t border-gray-700 pt-3">
            <button
              type="button"
              onClick={() => onChange('')}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-sm text-blue-400 hover:text-blue-300 transition-colors font-medium"
            >
              Done
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
